<template>
  <div>
    <div class="campaign-period">
      <Panel>
        <FormItemLabel label="キャンペーン期間" required />

        <div class="period-select">
          <div class="period-select-row">
            <el-date-picker
              :value="form.start_date"
              :disabled="is_start_datetime_disabled"
              :editable="false"
              :picker-options="start_date_picker_options"
              placeholder="開始日"
              prefix-icon="mdi mdi-calendar-today"
              type="date"
              value-format="yyyy-MM-dd"
              @input="changeStartDate"
            />

            <div class="campaign-time-select">
              <el-time-select
                :value="form.start_time"
                :disabled="is_start_datetime_disabled"
                :editable="false"
                :picker-options="time_picker_options"
                placeholder="開始時間"
                prefix-icon="mdi mdi-clock"
                @input="changeStartTime"
              />
            </div>

            <span class="label-suffix">以降</span>
          </div>

          <div class="period-select-row">
            <el-date-picker
              :value="form.end_date"
              :disabled="is_end_datetime_disabled"
              :editable="false"
              :picker-options="end_date_picker_options"
              placeholder="終了日"
              prefix-icon="mdi mdi-calendar-today"
              type="date"
              value-format="yyyy-MM-dd"
              @input="changeEndDate"
            />

            <div class="campaign-time-select">
              <el-time-select
                :value="form.end_time"
                :disabled="is_end_datetime_disabled"
                :editable="false"
                :picker-options="end_time_picker_options"
                placeholder="終了時間"
                prefix-icon="mdi mdi-clock"
                @input="changeEndTime"
              />
            </div>

            <span class="label-suffix">未満</span>
          </div>
        </div>

        <Tips class="condition-tips" v-if="is_tiktok">
          開始時間は、キャンペーン投稿が公開される1時間前に設定することをおすすめします。
        </Tips>

        <div v-if="is_campaign_period_empty" class="input-invalid-text">キャンペーン期間を入力してください。</div>

        <div v-if="is_start_datetime_error" class="input-invalid-text">
          キャンペーン開始日時は、現在日時より後を指定してください。
        </div>

        <div v-else-if="is_end_date_time_before_now" class="input-invalid-text">
          キャンペーン終了日時は、現在日時より後を指定してください。
        </div>

        <div v-else-if="is_end_datetime_before_start_datetime" class="input-invalid-text">
          キャンペーン終了日時は、開始日時より後を指定してください。
        </div>

        <div v-else-if="is_campaign_max_over" class="input-invalid-text">
          同時開催可能件数を超えるため、指定の期間ではキャンペーンを開催できません。
          <br />
          詳しくは
          <a href="https://help-atelu.comnico.jp/campaign-count-conditions" target="_blank"> こちら </a>
          をご確認ください。
        </div>

        <div v-else-if="is_web_instantwin_period_error" class="input-invalid-text">
          X APIの仕様変更により、{{ ntt_aaa_switchover_date_text }}以降を含む期間ではX
          WEBインスタントウィンを開催できません。
          <br />
          キャンペーン期間を変更してください。
        </div>
      </Panel>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment-timezone'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import Message from '@/client/components/_atoms/Message.vue'
import Panel from '@/client/components/_atoms/Panel.vue'
import Tips from '@/client/components/_atoms/Tips.vue'
import FormItemLabel from '@/client/components/_molecules/FormItemLabel.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import { TrackingService } from '@/client/services'
import { useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'
import { getNttAaaSwitchoverAt } from '@/common/utils'

export default defineComponent({
  name: 'CampaignCreatePeriod',
  components: {
    FormItemLabel,
    Message,
    Panel,
    Tips
  },
  computed: {
    ...mapState(useCampaignDialogStore, [
      'form',
      'mode',
      'api_campaign',
      'is_max_concurrent_campaign',
      'is_start_datetime_error',
      'is_campaign_period_empty',
      'is_end_datetime_before_start_datetime',
      'is_end_date_time_before_now',
      'is_web_instantwin_period_invalid',
      'ntt_aaa_switchover_date_text'
    ]),
    /** 期間フォームに切替日時のエラーを表示するか。編集の X WEB インスタントウィンのみ対象 */
    is_web_instantwin_period_error() {
      return this.is_web_instantwin_period_target && this.is_web_instantwin_period_invalid
    },
    /**
     * 期間に切替日時の制限をかける対象か。
     * 新規作成では期間を切替日時またぎに設定できるようにし、応募条件でエラーを知らせる。
     */
    is_web_instantwin_period_target() {
      return (
        this.mode === 'edit' && this.form.target_sns === 'twitter' && this.form.campaign_type === 'old_web_instantwin'
      )
    },
    /** 切替日時。制限の判定はカレンダーの表示と揃えるためブラウザのタイムゾーンで扱う */
    ntt_aaa_switchover_at() {
      return moment(getNttAaaSwitchoverAt())
    },
    is_start_datetime_disabled() {
      if (!this.api_campaign || this.mode === 'create' || this.mode === 'copy') {
        return false
      }

      return this.api_campaign.status !== CAMPAIGN_STATUS.BEFORE_EVENT
    },
    is_end_datetime_disabled() {
      if (!this.api_campaign || this.mode === 'create' || this.mode === 'copy') {
        return false
      }

      return [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION, CAMPAIGN_STATUS.COLLECTION_COMPLETED].includes(
        this.api_campaign.status
      )
    },
    start_date_picker_options() {
      return {
        disabledDate: date => {
          if (this.form.end_date) {
            return (
              moment(date.getTime()).isBefore(moment().startOf('day')) ||
              moment(date.getTime()).isAfter(moment(this.form.end_date))
            )
          }
          return moment(date.getTime()).isBefore(moment().startOf('day'))
        }
      }
    },
    end_date_picker_options() {
      return {
        disabledDate: date => {
          // 切替日時より後まで開催する X WEB インスタントウィンは応募を収集できないため切替日の翌日以降を選ばせない.
          if (
            this.is_web_instantwin_period_target &&
            moment(date.getTime()).isAfter(this.ntt_aaa_switchover_at, 'day')
          ) {
            return true
          }

          if (this.form.start_date) {
            return (
              moment(date.getTime()).isBefore(moment().startOf('day')) ||
              moment(date.getTime()).isBefore(moment(this.form.start_date))
            )
          }
          return moment(date.getTime()).isBefore(moment().startOf('day'))
        }
      }
    },
    time_picker_options() {
      return {
        start: '00:00',
        step: '01:00',
        end: '23:00'
      }
    },
    /** 終了時間の選択肢。終了日が切替日当日の場合は切替時刻までに絞る */
    end_time_picker_options() {
      if (this.is_web_instantwin_period_target && this.is_end_date_on_ntt_aaa_switchover_date) {
        return {
          ...this.time_picker_options,
          end: this.ntt_aaa_switchover_at.format('HH:mm')
        }
      }

      return this.time_picker_options
    },
    /** 終了日が切替日と同じ日か */
    is_end_date_on_ntt_aaa_switchover_date() {
      if (!this.form.end_date) {
        return false
      }

      return moment(this.form.end_date, 'YYYY-MM-DD').isSame(this.ntt_aaa_switchover_at, 'day')
    },
    is_campaign_max_over() {
      if (!this.form.start_date || !this.form.start_time || !this.form.end_date || !this.form.end_time) {
        return false
      }

      return this.is_max_concurrent_campaign
    },
    is_twitter() {
      return this.form.target_sns === 'twitter'
    },
    is_instagram() {
      return this.form.target_sns === 'instagram'
    },
    is_tiktok() {
      return this.form.target_sns === 'tiktok'
    }
  },
  methods: {
    ...mapActions(useCampaignDialogStore, ['setForm', 'checkCampaign']),
    cancel(payload: unknown) {
      this.$emit('cancel', payload)
      return payload
    },
    /**
     * 開始日の変更
     */
    async changeStartDate(value: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|期間|開始日')

      await this.setForm({
        ...this.form,
        start_date: value
      })

      await this.checkIsMaxConcurrentCampaign()
    },
    /**
     * 開始時間の変更
     */
    async changeStartTime(value: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|期間|開始時間')

      await this.setForm({
        ...this.form,
        start_time: value
      })

      await this.checkIsMaxConcurrentCampaign()
    },
    /**
     * 終了日の変更
     */
    async changeEndDate(value: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|期間|終了日')

      await this.setForm({
        ...this.form,
        end_date: value,
        end_time: this.adjustEndTimeToSwitchover(value)
      })

      await this.checkIsMaxConcurrentCampaign()
    },
    /**
     * 終了日の変更に合わせて終了時間を切替時刻へ収める。
     * 切替日当日は切替時刻までしか選べないため、選択済みの時間がそれより後なら切替時刻へ揃える。
     */
    adjustEndTimeToSwitchover(end_date: string) {
      if (!this.is_web_instantwin_period_target || !this.form.end_time) {
        return this.form.end_time
      }

      const end_datetime = moment(`${end_date} ${this.form.end_time}`, 'YYYY-MM-DD HH:mm')

      if (end_datetime.isSameOrBefore(this.ntt_aaa_switchover_at)) {
        return this.form.end_time
      }

      return this.ntt_aaa_switchover_at.format('HH:mm')
    },
    /**
     * 終了時間の変更
     */
    async changeEndTime(value: string) {
      TrackingService.sendEvent('select:キャンペーンの新規作成|期間|終了時間')

      await this.setForm({
        ...this.form,
        end_time: value
      })

      await this.checkIsMaxConcurrentCampaign()
    },
    /**
     * 同時開催数上限を超えることをチェック
     */
    async checkIsMaxConcurrentCampaign() {
      if (
        this.is_start_datetime_error ||
        this.is_campaign_period_empty ||
        this.is_end_datetime_before_start_datetime ||
        this.is_end_date_time_before_now ||
        this.form.campaign_type === 'old_web_instantwin' ||
        this.mode !== 'edit'
      ) {
        return
      }

      const payload = {
        start_datetime: `${this.form.start_date} ${this.form.start_time}`,
        end_datetime: `${this.form.end_date} ${this.form.end_time}`,
        exclude_campaign_id: this.mode === 'edit' && this.api_campaign ? this.api_campaign.id : 0
      }

      await this.checkCampaign(payload)
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.note-message {
  margin-top: $spacing-unit * 0.5;
  font-size: 12px;
}

.campaign-period {
  margin-top: $spacing-unit * 2;

  .period-select {
    margin-top: $spacing-unit;

    .period-select-row:not(:first-child) {
      margin-top: $spacing-unit;
    }

    .period-select-row {
      display: flex;
      align-items: center;

      .campaign-time-select {
        margin-left: $spacing-unit;
      }

      .label-suffix {
        margin-left: $spacing-unit;
      }
    }
  }

  :deep(.el-input__prefix) {
    .mdi {
      align-items: inherit;
      vertical-align: inherit;
    }
  }
}

.period-tips {
  margin-top: $spacing-unit;
}

.condition-tips {
  margin-top: $spacing-unit;
  white-space: nowrap;
}

.input-invalid-text {
  margin-top: $spacing-unit;
  color: #ef5350;
}
</style>
